import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

type Status = "new_submission" | "review" | "offer_sent" | "accepted" | "completed";

type Challenge = {
  id: string;
  created_at: string;
  email: string;
  idea_submission: string;
  status: Status;
  partner_name: string | null;
  partner_email: string | null;
  partner_linkedin: string | null;
  partner_x_handle: string | null;
  partner_youtube: string | null;
  partner_instagram: string | null;
  community_link: string | null;
  newsletter_link: string | null;
  coupon_code: string | null;
  discount_percentage: number | null;
  offer_uuid: string | null;
  offer_data: any;
};

const Offers = () => {
  const { isAdmin, loading, signOut } = useAdminAuth();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({
    partner_name: "",
    partner_email: "",
    partner_linkedin: "",
    partner_x_handle: "",
    partner_youtube: "",
    partner_instagram: "",
    coupon_code: "",
    discount_percentage: 15,
    community_link: "",
    newsletter_link: "",
    idea_submission: ""
  });

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    const { data } = await supabase
      .from('challenges')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setChallenges(data as Challenge[]);
    }
  };

  const handleOpenOffer = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
    setOfferForm({
      partner_name: challenge.partner_name || "",
      partner_email: challenge.partner_email || "",
      partner_linkedin: challenge.partner_linkedin || "",
      partner_x_handle: challenge.partner_x_handle || "",
      partner_youtube: challenge.partner_youtube || "",
      partner_instagram: challenge.partner_instagram || "",
      coupon_code: challenge.coupon_code || "",
      discount_percentage: challenge.discount_percentage || 15,
      community_link: (challenge as any).community_link || "",
      newsletter_link: (challenge as any).newsletter_link || "",
      idea_submission: challenge.idea_submission || ""
    });
    setDialogOpen(true);
  };

  const handleCreateNewOffer = () => {
    setSelectedChallenge(null);
    setOfferForm({
      partner_name: "",
      partner_email: "",
      partner_linkedin: "",
      partner_x_handle: "",
      partner_youtube: "",
      partner_instagram: "",
      coupon_code: "",
      discount_percentage: 15,
      community_link: "",
      newsletter_link: "",
      idea_submission: ""
    });
    setDialogOpen(true);
  };

  const handleSaveOffer = async () => {
    if (!offerForm.partner_name || !offerForm.coupon_code) {
      toast.error("Partner name and coupon code are required");
      return;
    }

    if (!selectedChallenge && !offerForm.idea_submission) {
      toast.error("Idea submission is required for new offers");
      return;
    }

    // Generate a secure random UUID for the offer
    const offerUuid = crypto.randomUUID();

    // If creating new offer
    if (!selectedChallenge) {
      const { error } = await supabase
        .from('challenges')
        .insert({
          email: offerForm.partner_email,
          idea_submission: offerForm.idea_submission,
          partner_name: offerForm.partner_name,
          partner_email: offerForm.partner_email,
          partner_linkedin: offerForm.partner_linkedin,
          partner_x_handle: offerForm.partner_x_handle,
          partner_youtube: offerForm.partner_youtube,
          partner_instagram: offerForm.partner_instagram,
          coupon_code: offerForm.coupon_code,
          discount_percentage: offerForm.discount_percentage,
          community_link: offerForm.community_link,
          newsletter_link: offerForm.newsletter_link,
          offer_uuid: offerUuid,
          status: 'accepted'
        });

      if (error) {
        toast.error("Failed to create offer");
      } else {
        toast.success("Offer created successfully");
        setDialogOpen(false);
        fetchChallenges();
      }
    } else {
      // If updating existing offer - keep existing UUID or generate new one
      const updateData = {
        partner_name: offerForm.partner_name,
        partner_email: offerForm.partner_email,
        partner_linkedin: offerForm.partner_linkedin,
        partner_x_handle: offerForm.partner_x_handle,
        partner_youtube: offerForm.partner_youtube,
        partner_instagram: offerForm.partner_instagram,
        coupon_code: offerForm.coupon_code,
        discount_percentage: offerForm.discount_percentage,
        community_link: offerForm.community_link,
        newsletter_link: offerForm.newsletter_link,
        offer_uuid: selectedChallenge.offer_uuid || offerUuid,
        status: 'accepted' as Status
      };

      const { error } = await supabase
        .from('challenges')
        .update(updateData)
        .eq('id', selectedChallenge.id);

      if (error) {
        toast.error("Failed to save offer");
      } else {
        toast.success("Offer saved successfully");
        setDialogOpen(false);
        fetchChallenges();
      }
    }
  };

  const copyOfferUrl = (offerUuid: string) => {
    const url = `${window.location.origin}/offer/${offerUuid}`;
    navigator.clipboard.writeText(url);
    toast.success("Offer URL copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-4">You don't have admin privileges.</p>
          <Button asChild>
            <Link to="/admin/login">Return to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  const columns = [
    { id: "new_submission", title: "New Submission", color: "bg-blue-500/10 border-blue-500/20" },
    { id: "review", title: "In Review", color: "bg-yellow-500/10 border-yellow-500/20" },
    { id: "offer_sent", title: "Offer Sent", color: "bg-purple-500/10 border-purple-500/20" },
    { id: "accepted", title: "Accepted", color: "bg-green-500/10 border-green-500/20" },
    { id: "completed", title: "Completed", color: "bg-gray-500/10 border-gray-500/20" }
  ];

  const getSubmissionsForColumn = (columnId: string) => {
    return challenges.filter(c => c.status === columnId);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <img src="/assets/logo.png" alt="OneWeekMVPs" className="h-8" />
              <nav className="flex gap-6">
                <Link to="/admin/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Dashboard
                </Link>
                <Link to="/admin/quotes" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Quotes
                </Link>
                <Link to="/admin/offers" className="text-sm font-medium text-foreground">
                  Offers
                </Link>
              </nav>
            </div>
            <Button variant="outline" onClick={signOut}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl mb-2">Partner Offers</h1>
            <p className="text-muted-foreground">Manage challenge submissions and create partner offers</p>
          </div>
          <Button onClick={handleCreateNewOffer}>
            Create New Offer
          </Button>
        </div>

        {/* Kanban Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {columns.map((column) => (
            <div key={column.id} className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg">{column.title}</h3>
                <Badge variant="outline">{getSubmissionsForColumn(column.id).length}</Badge>
              </div>
              
              <div className="space-y-3">
                {getSubmissionsForColumn(column.id).map((submission) => (
                  <Card 
                    key={submission.id} 
                    className={`p-4 cursor-pointer hover:border-accent/40 transition-colors ${column.color}`}
                  >
                    <div className="mb-2">
                      {submission.partner_name ? (
                        <h4 className="font-heading text-base mb-1">{submission.partner_name}</h4>
                      ) : (
                        <h4 className="font-heading text-base mb-1 text-muted-foreground">New Submission</h4>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{submission.idea_submission}</p>
                    <p className="text-sm text-muted-foreground mb-2">{submission.email}</p>
                    <p className="text-xs text-muted-foreground mb-3">{new Date(submission.created_at).toLocaleDateString()}</p>
                    
                    {submission.offer_uuid && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full mb-2"
                        onClick={() => copyOfferUrl(submission.offer_uuid!)}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy URL
                      </Button>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleOpenOffer(submission)}
                    >
                      Manage Offer
                    </Button>
                  </Card>
                ))}

                {getSubmissionsForColumn(column.id).length === 0 && (
                  <Card className="p-6 text-center border-dashed">
                    <p className="text-sm text-muted-foreground">No submissions</p>
                  </Card>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Offer Management Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedChallenge ? 'Manage Partner Offer' : 'Create New Offer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {selectedChallenge && (
              <div>
                <h3 className="font-semibold mb-2">Submission Details</h3>
                <p className="text-sm mb-1"><span className="font-medium">Email:</span> {selectedChallenge.email}</p>
                <p className="text-sm mb-1"><span className="font-medium">Idea:</span> {selectedChallenge.idea_submission}</p>
                <p className="text-sm"><span className="font-medium">Date:</span> {new Date(selectedChallenge.created_at).toLocaleString()}</p>
              </div>
            )}

            {!selectedChallenge && (
              <div>
                <Label htmlFor="idea_submission">Idea / Project Description *</Label>
                <Textarea
                  id="idea_submission"
                  value={offerForm.idea_submission}
                  onChange={(e) => setOfferForm({...offerForm, idea_submission: e.target.value})}
                  placeholder="Describe the MVP idea or project..."
                  rows={4}
                />
              </div>
            )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="partner_name">Partner Name *</Label>
                  <Input
                    id="partner_name"
                    value={offerForm.partner_name}
                    onChange={(e) => setOfferForm({...offerForm, partner_name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="partner_email">Partner Email *</Label>
                  <Input
                    id="partner_email"
                    value={offerForm.partner_email}
                    onChange={(e) => setOfferForm({...offerForm, partner_email: e.target.value})}
                    placeholder="partner@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="coupon_code">Coupon Code *</Label>
                  <Input
                    id="coupon_code"
                    value={offerForm.coupon_code}
                    onChange={(e) => setOfferForm({...offerForm, coupon_code: e.target.value})}
                    placeholder="PARTNER50"
                  />
                </div>
                <div>
                  <Label htmlFor="discount_percentage">Discount Percentage</Label>
                  <Input
                    id="discount_percentage"
                    type="number"
                    value={offerForm.discount_percentage}
                    onChange={(e) => setOfferForm({...offerForm, discount_percentage: Number(e.target.value)})}
                    placeholder="15"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Social Links (Optional)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="partner_linkedin">LinkedIn</Label>
                    <Input
                      id="partner_linkedin"
                      value={offerForm.partner_linkedin}
                      onChange={(e) => setOfferForm({...offerForm, partner_linkedin: e.target.value})}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="partner_x_handle">X (Twitter) Handle</Label>
                    <Input
                      id="partner_x_handle"
                      value={offerForm.partner_x_handle}
                      onChange={(e) => setOfferForm({...offerForm, partner_x_handle: e.target.value})}
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="partner_youtube">YouTube</Label>
                    <Input
                      id="partner_youtube"
                      value={offerForm.partner_youtube}
                      onChange={(e) => setOfferForm({...offerForm, partner_youtube: e.target.value})}
                      placeholder="https://youtube.com/@..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="partner_instagram">Instagram</Label>
                    <Input
                      id="partner_instagram"
                      value={offerForm.partner_instagram}
                      onChange={(e) => setOfferForm({...offerForm, partner_instagram: e.target.value})}
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="community_link">Community Link</Label>
                    <Input
                      id="community_link"
                      value={offerForm.community_link}
                      onChange={(e) => setOfferForm({...offerForm, community_link: e.target.value})}
                      placeholder="https://community.example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newsletter_link">Newsletter Link</Label>
                    <Input
                      id="newsletter_link"
                      value={offerForm.newsletter_link}
                      onChange={(e) => setOfferForm({...offerForm, newsletter_link: e.target.value})}
                      placeholder="https://newsletter.example.com"
                    />
                  </div>
                </div>
              </div>

              {selectedChallenge?.offer_uuid && (
                <div className="p-4 bg-accent/10 rounded-lg">
                  <Label>Offer URL</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={`${window.location.origin}/offer/${selectedChallenge?.offer_uuid}`}
                      readOnly
                    />
                    <Button
                      variant="outline"
                      onClick={() => copyOfferUrl(selectedChallenge?.offer_uuid!)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open(`/offer/${selectedChallenge?.offer_uuid}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveOffer}>
                  Save & Generate URL
                </Button>
              </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Offers;
